import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DocumentCard from "@/components/DocumentCard";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LayoutTemplate, Loader2, ChevronDown } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

interface DocumentsSectionProps {
  searchQuery: string;
}

const DocumentsSection = ({ searchQuery }: DocumentsSectionProps) => {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const [activeCategory, setActiveCategory] = useState(categoryParam || "Marketing");
  
  useEffect(() => {
    if (categoryParam) {
      setActiveCategory(categoryParam);
    }
  }, [categoryParam]);

  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      console.log('Fetching templates...');
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .not('file_path', 'like', '%.emptyFolderPlaceholder')
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Error fetching templates:', error);
        throw error;
      }
      
      console.log('Fetched templates:', data);
      return data;
    }
  });

  if (error) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg border border-destructive/20">
          Error loading templates. Please try again later.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Filter templates based on search query
  const filteredTemplates = templates?.filter(template => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      template.title.toLowerCase().includes(searchLower) ||
      template.description?.toLowerCase().includes(searchLower) ||
      template.category.toLowerCase().includes(searchLower)
    );
  });

  // Group filtered templates by category
  const groupedTemplates = filteredTemplates?.reduce((acc, template) => {
    const category = template.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<string, typeof templates>);

  // Define the categories in the order we want them to appear
  const categories = ["Marketing", "Excel Files", "Finance", "KPI Dashboard", "HR Presentations", "Construction Planning", "Business", "Figma Templates", "Modern Presentation"];

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    const tabsList = document.querySelector('[role="tablist"]');
    const trigger = tabsList?.querySelector(`[value="${category}"]`) as HTMLButtonElement;
    if (trigger) {
      trigger.click();
    }
  };

  return (
    <div id="resources" className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      {searchQuery && (
        <div className="mb-8">
          <p className="text-muted-foreground">
            Showing results for "<span className="text-foreground font-medium">{searchQuery}</span>"
          </p>
        </div>
      )}
      
      <Tabs value={activeCategory} onValueChange={setActiveCategory} defaultValue="Marketing" className="w-full">
        {isMobile ? (
          <div className="mb-8 relative">
            <div className="relative">
              <select 
                className="w-full py-4 px-4 text-base rounded-lg border border-border bg-card text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none pr-10"
                value={activeCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                style={{ height: '3.5rem' }}
              >
                {categories.map((category) => (
                  <option 
                    key={category} 
                    value={category}
                    className="py-3"
                  >
                    {category} ({groupedTemplates?.[category]?.length || 0})
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center mb-8 overflow-x-auto pb-2">
            <TabsList className="bg-secondary p-1 rounded-lg">
              {categories.map((category) => (
                <TabsTrigger 
                  key={category} 
                  value={category}
                  className="px-4 py-2 rounded-md transition-all duration-200 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm text-muted-foreground hover:text-foreground"
                >
                  {category} ({groupedTemplates?.[category]?.length || 0})
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        )}

        {categories.map((category) => (
          <TabsContent key={category} value={category} className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {groupedTemplates?.[category]?.map((doc) => (
                <DocumentCard 
                  key={doc.id}
                  title={doc.title}
                  type={doc.type}
                  downloadUrl={doc.file_path}
                  description={doc.description}
                  createdAt={doc.created_at}
                />
              ))}
              {(!groupedTemplates?.[category] || groupedTemplates[category].length === 0) && (
                <div className="col-span-full">
                  <div className="text-center py-16 px-4 rounded-xl bg-secondary/50 border border-border">
                    <LayoutTemplate className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground font-medium">
                      No templates available in this category yet
                    </p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default DocumentsSection;
